import type { Attachment } from '@medplum/fhirtypes'
import { cn } from '@seen/ui'
import { IconAlertTriangle, IconFileOff } from '@tabler/icons-react'
import { memo, useState } from 'react'
import { AttachmentDisplay } from '#src/components/attachments/AttachmentDisplay.tsx'
import { RetryConversionButton } from '#src/components/attachments/RetryConversionButton.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { DocumentPickerEmptyState } from '#src/components/document-picker/DocumentPickerEmptyState.tsx'
import type { DocumentPickerDocument } from '#src/components/document-picker/DocumentPickerView.tsx'
import { useAttachmentConversionStatus } from '#src/hooks/useAttachmentConversionStatus.ts'
import { useStableAttachmentUrl } from '#src/hooks/useStableAttachmentUrl.ts'

interface DocumentPickerPreviewProps {
  document?: DocumentPickerDocument | null
  className?: string
}

export function DocumentPickerPreview({
  document,
  className,
}: DocumentPickerPreviewProps) {
  if (!document) {
    return (
      <DocumentPickerEmptyState
        title="No document selected"
        description="Select a document to preview"
        className={cn('h-full', className)}
      />
    )
  }

  const attachments = document.attachments ?? []

  if (attachments.length === 0) {
    return <PreviewNotAvailable title={document.title} className={className} />
  }

  if (attachments.length === 1) {
    const attachment = attachments[0]
    if (attachment.contentType === 'application/pdf' && attachment.url) {
      return (
        <PDFPreview
          documentUrl={attachment.url}
          title={document.title}
          className={cn('h-full w-full border-0', className)}
        />
      )
    }

    if (attachment.contentType?.startsWith('image/') && attachment.url) {
      return (
        <ImagePreview
          attachment={attachment}
          documentUrl={attachment.url}
          title={document.title}
          documentReferenceId={document.id}
          className={className}
        />
      )
    }

    return <PreviewNotAvailable title={document.title} className={className} />
  }

  return (
    <ScrollArea scrollRestorationId={null} className={cn('h-full', className)}>
      <div className="grid grid-cols-2 gap-4 p-4">
        {attachments.map((attachment, i) => (
          <div key={attachment.url ?? i}>
            <AttachmentDisplay
              value={attachment}
              documentReferenceId={document.id}
            />
          </div>
        ))}
      </div>
    </ScrollArea>
  )
}

function PreviewNotAvailable({
  title,
  className,
}: {
  title: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground',
        className
      )}
    >
      <IconFileOff className="size-10 stroke-1" />
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs">Preview not available</p>
    </div>
  )
}

function PDFPreview({
  documentUrl,
  title,
  className,
}: {
  documentUrl: string
  title: string
  className?: string
}) {
  const stableUrl = useStableAttachmentUrl(documentUrl)

  if (!stableUrl) {
    return <PreviewNotAvailable title={title} className={className} />
  }

  return (
    <MemoizedPDFPreview
      cachedUrl={stableUrl}
      title={title}
      className={className}
    />
  )
}

const MemoizedPDFPreview = memo(function MemoizedPDFPreview({
  cachedUrl,
  title,
  className,
}: {
  cachedUrl: string
  title: string
  className?: string
}) {
  return (
    <iframe
      src={`${cachedUrl}#view=FitH&toolbar=0&navpanes=0`}
      title={title}
      className={cn('h-full w-full border-0', className)}
    />
  )
})

function ImagePreview({
  attachment,
  documentUrl,
  title,
  documentReferenceId,
  className,
}: {
  attachment: Attachment
  documentUrl: string
  title: string
  documentReferenceId: string
  className?: string
}) {
  const { status } = useAttachmentConversionStatus(attachment)
  const stableUrl = useStableAttachmentUrl(documentUrl)

  if (status === 'pending') {
    return (
      <div className={cn('relative h-full w-full', className)}>
        <Skeleton
          role="status"
          aria-label="File converting"
          className="h-full w-full"
        />
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          Converting...
        </p>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div
        role="alert"
        className={cn(
          'flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground',
          className
        )}
      >
        <IconAlertTriangle className="size-10 stroke-1 text-amber-500" />
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs">
          Preview unavailable — this file couldn&apos;t be converted
        </p>
        <RetryConversionButton
          attachment={attachment}
          documentReferenceId={documentReferenceId}
        />
      </div>
    )
  }

  if (!stableUrl) {
    return <PreviewNotAvailable title={title} className={className} />
  }

  return (
    <MemoizedImagePreview
      cachedUrl={stableUrl}
      title={title}
      className={className}
    />
  )
}

const MemoizedImagePreview = memo(function MemoizedImagePreview({
  cachedUrl,
  title,
  className,
}: {
  cachedUrl: string
  title: string
  className?: string
}) {
  const [hasError, setHasError] = useState(false)

  if (hasError) {
    return <PreviewNotAvailable title={title} className={className} />
  }

  return (
    <div
      className={cn('flex h-full items-center justify-center p-4', className)}
    >
      <img
        src={cachedUrl}
        alt={title}
        className="max-h-full max-w-full object-contain"
        onError={() => setHasError(true)}
      />
    </div>
  )
})
